// Root layout used by createBrowserRouter. Wraps every child route's element
// in framer-motion's AnimatePresence so navigations cross-fade instead of
// snapping. Keys on location.pathname so unrelated state transitions (e.g.,
// URL params on the same route) don't re-mount.
//
// HISTORY — this used to carry `className="contents"` on the animating div.
// `display: contents` generates no layout box, so opacity and transform had
// nothing to apply to: the declared crossfade was a no-op and every
// navigation in the app was a hard cut. Verified in-browser
// (getClientRects().length === 0) before the fix. The wrapper is now a real
// box; do not put `display: contents` back on it.
//
// Per-pair descriptors let a specific navigation opt out of the generic fade.
// The Welcome → Menu tape transport draws its own full-screen cover, so the
// route swap underneath must be instant — a crossfade there would be a second,
// competing transition happening behind the first.
//
// Reduced motion still gets a transition, just a shorter opacity-only one.
// "Reduced" should mean less travel, not nothing happening.
//
// Also hosts the multi-match park/load sync. The active match lives in
// useMatchStore (singleton); the sidecar useMatchesStore holds parked
// snapshots of every other in-flight match. On every URL change we
// either park (leaving a game route) or swap (entering a different
// game's URL). See `src/stores/matches.ts` for the contract.

import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffectiveReducedMotion } from './useReducedMotion';
import { CardDetailHost } from './CardDetailHost';
import { HeroDetailHost } from './HeroDetailHost';
import { useTransitionActive } from '../stores/transition';
import { duration, ease } from '../theme/motion';
import { useMatchStore } from '../stores/match';
import {
  useMatchesStore,
  parkCurrent,
  loadIntoActive,
  migrateLegacySingleMatch,
} from '../stores/matches';

// Pull the :matchId param out of `/game/:matchId/...`. Returns null for
// any other path. Done with regex so RouteLayout doesn't need to be a
// child of an outlet that has useParams.
function readMatchIdFromPath(path: string): string | null {
  const m = path.match(/^\/game\/([^/]+)/);
  return m ? m[1] : null;
}

function useMatchRouteSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeMatchId = useMatchStore((s) => s.matchId);

  // One-time migration of the legacy single-match into the sidecar.
  // No-op after the first run (idempotent in matches.ts).
  useEffect(() => {
    migrateLegacySingleMatch();
    // Repairs match rows stranded 'in-progress' by the shipped results-screen
    // ordering bug, which otherwise permanently consume cap slots and block
    // new games. Safe on every boot — a no-op once there is nothing to fix.
    useMatchesStore.getState().reconcileFinished();
  }, []);

  useEffect(() => {
    const urlMatchId = readMatchIdFromPath(location.pathname);

    if (urlMatchId === null) {
      // Left the game routes entirely — park current so the hub /
      // any other route reads the latest snapshot.
      parkCurrent();
      return;
    }

    if (urlMatchId === activeMatchId) {
      // Same match still active. Ensure the sidecar has an entry —
      // covers brand-new matches created by `newMatch()` which set the
      // store but haven't been parked yet.
      if (!useMatchesStore.getState().matches[urlMatchId]) {
        parkCurrent();
      }
      return;
    }

    // Switching matches — park what's loaded, load what's requested.
    parkCurrent();
    const loaded = loadIntoActive(urlMatchId);
    if (!loaded) {
      // URL points at a match we have no snapshot for (e.g. user
      // pasted a stale deep-link, or storage was cleared). Bounce to
      // the hub so they can pick a real one.
      navigate('/matches', { replace: true });
    }
  }, [location.pathname, activeMatchId, navigate]);
}

/** How a given navigation should be presented. */
type RouteTransition =
  /** Default: short opacity + lift crossfade. */
  | 'fade'
  /** Something else owns the visuals (currently: the tape transport). */
  | 'none';

/**
 * Overrides keyed `from>to`. Deliberately sparse — every entry is a claim that
 * this specific navigation is special, and most aren't.
 */
const PAIR_TRANSITIONS: Record<string, RouteTransition> = {
  '/welcome>/menu': 'none',
};

function resolveTransition(from: string | null, to: string, tapeActive: boolean): RouteTransition {
  // The tape cover is opaque across the swap; anything we did here would
  // happen behind it, cost frames, and risk fighting the sequence.
  if (tapeActive) return 'none';
  if (from && PAIR_TRANSITIONS[`${from}>${to}`]) return PAIR_TRANSITIONS[`${from}>${to}`];
  return 'fade';
}

export function RouteLayout() {
  const location = useLocation();
  const reduced = useEffectiveReducedMotion();
  const tapeActive = useTransitionActive();
  useMatchRouteSync();

  // Previous pathname, for pair lookup. A ref rather than state: it must not
  // itself trigger a render, and it's read during render only to classify the
  // navigation that has already happened.
  const prevPath = useRef<string | null>(null);
  const from = prevPath.current;
  useEffect(() => {
    prevPath.current = location.pathname;
  }, [location.pathname]);

  const animated = resolveTransition(from, location.pathname, tapeActive) === 'fade';

  // The element structure below must stay IDENTICAL across every value of
  // `animated`. An earlier revision returned a bare <div> for the 'none' case
  // and the AnimatePresence tree otherwise — switching between those two
  // shapes unmounted and remounted the entire route subtree the moment the
  // tape transition started, which visibly restarted Welcome's entrance
  // animation underneath the still-opening cover. Branch on the props, never
  // on the tree.
  return (
    <>
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={animated ? { opacity: 0, y: reduced ? 0 : 4 } : false}
        animate={{ opacity: 1, y: 0 }}
        exit={animated ? { opacity: 0, y: reduced ? 0 : -4 } : { opacity: 1 }}
        transition={
          animated
            ? { duration: reduced ? duration.tap : duration.hint, ease: ease.out }
            : { duration: 0 }
        }
        // A real box — the route content still owns its own h-dvh layout, but
        // this element must generate a box or opacity/transform do nothing.
        className="h-full w-full"
      >
        <Outlet />
      </motion.div>
    </AnimatePresence>
    {/* Outside the crossfade on purpose — see CardDetailHost. */}
    <CardDetailHost />
    <HeroDetailHost />
    </>
  );
}
